import { DestroyRef, inject, Inject, Injectable, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

import { NgAceActionService } from '../services/ace-action.service';
import { NgAceCardActionService } from '../services/ace-card-action.service';
import { NgAceSidebarActionService } from '../services/ace-sidebar-action.service';

import Util from '@ace/util';

@Injectable()
export abstract class AceActionHandler {
  protected readonly destroyRef = inject(DestroyRef);

  element!: HTMLElement;
  prevented: Record<string, boolean>;

  protected actionService: NgAceActionService;

  constructor(
    // eslint-disable-next-line @angular-eslint/prefer-inject
    protected injector: Injector,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    @Inject('type') private readonly type?: number,
  ) {
    // use a different Action Service for sidebar, cards, and other elements
    // not necessary, we can use just one Action Service altogether
    // but maybe it becomes more responsive this way

    // the type paramter is passed in Directives
    if (this.type === 1) {
      this.actionService = this.injector.get(NgAceSidebarActionService);
    } else if (this.type === 2) {
      this.actionService = this.injector.get(NgAceCardActionService);
    } else {
      this.actionService = this.injector.get(NgAceActionService);
    }

    this.prevented = {};
  }

  init(): void {
    this._subscribeToServices();
  }

  private _subscribeToServices(): void {
    this.actionService
      .onAction()
      .pipe(
        filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ({ selector, action }) => typeof (this as any)[action] === 'function' && Util.matches(this.element, selector),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data: { selector: string; action: string }) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any)[data.action](); // call the function with data.action name
      });

    this.actionService
      .onPrevent()
      .pipe(
        filter(({ selector }) => Util.matches(this.element, selector)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data: { selector: string; event: string; prevent?: boolean }) => {
        this.preventEvent(data.event, data.prevent);
      });
  }

  //
  preventEvent(event: string, prevent?: boolean) {
    this.prevented[event] = prevent === false ? false : true;
  }

  isPrevented(event: string): boolean {
    return this.prevented[event] === true;
  }
}
